import { createContext, useContext } from "react";
import type { DayLog, Note, NoteInput, Project, Task, TaskInput } from "../lib/api";

export interface TimerView {
  mode: "work" | "break";
  left: number; // seconds remaining
  running: boolean;
  workMin: number;
  brkMin: number;
  linkedTaskId: string | null;
}

export interface TimerApi extends TimerView {
  toggle: () => void;
  reset: () => void;
  setPreset: (workMin: number, brkMin: number) => void;
  setTask: (id: string | null) => void;
}

export interface AppApi {
  tasks: Task[];
  projects: Project[];
  dayLogs: DayLog[];
  notes: Note[];
  pmap: Map<string, Project>;
  today: string;
  todayLog: DayLog | undefined;
  isLoading: boolean;

  // Active project filter shared across the side menu and every view ("all" or a project id).
  filter: string;
  setFilter: (f: string) => void;

  createTask: (input: TaskInput & { title: string }) => string;
  updateTask: (id: string, patch: TaskInput) => void;
  deleteTask: (t: Task) => void;
  toggleDone: (t: Task) => void;
  toggleMit: (t: Task) => void;
  addProject: (name: string) => void;
  autoPlaceToday: () => void;
  rollStale: () => void;

  // Notes — returns the new note's id so the caller can open it immediately.
  createNote: (input?: NoteInput) => string;
  updateNote: (id: string, patch: NoteInput) => void;
  deleteNote: (id: string) => void;

  planDay: (pickedIds: Set<string>, autoPlace: boolean) => void;
  completeShutdown: () => void;

  openPalette: () => void;
  openHelp: () => void;
  openPlan: () => void;
  openShutdown: () => void;
  openFocus: (id?: string) => void;
  // Open the task editor: pass a task id to edit, or null/undefined for a new task.
  openTask: (id?: string | null) => void;

  toast: (message: string, undo?: () => void) => void;

  timer: TimerApi;
}

export const AppContext = createContext<AppApi | null>(null);

export function useApp(): AppApi {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within <AppProvider>");
  return ctx;
}
